import React, { useState, useEffect, useContext } from 'react';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import type { CustomDimensions, RequiredDimsGdpData } from '@zillow/universal-analytics-react';
import {
    UniversalAnalyticsContext,
    getRequiredCustomDimsForProperty,
} from '@zillow/universal-analytics-react';

import SearchInputWithButton from './SearchInputWithButton';
import ErrorBox from './ErrorBox';
import HomeDetailLightbox from './HomeDetailLightbox';
import {
    trackHmimhwPropertySearchDisplay,
    trackHmimhwPropertySearchError,
    trackAddressSearch,
} from '../utils/gaUtils';
import PropertySearch from '@zillow/property-search';
import { useYieldCallback } from '@zillow/yield-callback';
import { convertToLowerCaseParams } from '../utils/urlUtils';
import { getCurrentURLParams } from '../utils/windowUtils';

export interface Props {
    publicConfigValues: {
        addressAutocompleteURL: string;
        zatlasEndpoint: string;
    };
    placementId: string;
    surfaceId: string;
    xConsumerUsername: string;
    isLightboxBackgroundLocked?: boolean;
    isCMS?: boolean;
    setIsLightboxOpen?: (isOpen: boolean) => void;
    handleLightBoxDirectHit?: () => void;
    setRequiredCustomDimensions: React.Dispatch<React.SetStateAction<CustomDimensions | null>>;
}

const PropertySearchWrapper = ({
    publicConfigValues: { addressAutocompleteURL, zatlasEndpoint },
    placementId,
    surfaceId,
    xConsumerUsername,
    isLightboxBackgroundLocked = false,
    isCMS,
    setIsLightboxOpen,
    handleLightBoxDirectHit,
    setRequiredCustomDimensions,
}: Props) => {
    clearAllBodyScrollLocks();
    const { areRequiredDimensionsSet } = useContext(UniversalAnalyticsContext);
    const [zpid, setZpid] = useState<string>();
    const [isLightBoxDirectHit, setIsLightBoxDirectHit] = useState(false);
    const [alreadyDirectAutoOpen, setAlreadyDirectAutoOpen] = useState(false);
    const [hmimhwAnalyticsData, setHmimhwAnalyticsData] = useState<RequiredDimsGdpData | null>(
        null
    );
    useEffect(() => {
        const params = convertToLowerCaseParams(new URLSearchParams(getCurrentURLParams()));
        const urlZpid = params.get('zpid');
        let isInitialized = false;
        if (!isLightBoxDirectHit && !!urlZpid) {
            setIsLightBoxDirectHit(!!urlZpid);
            setZpid(urlZpid);
            isInitialized = true;
        }

        if (hmimhwAnalyticsData) {
            setRequiredCustomDimensions(
                getRequiredCustomDimsForProperty({
                    property: hmimhwAnalyticsData.property,
                    abTests: hmimhwAnalyticsData.abTests,
                })
            );
        }

        setIsLightboxOpen?.(!!zpid);

        if (!zpid && !areRequiredDimensionsSet && !isInitialized) {
            trackHmimhwPropertySearchDisplay(placementId, surfaceId);
        }

        // only fire handleLightBoxDirectHit
        // if directly hitting the hmimhw page with the Review lightbox
        if (
            isLightBoxDirectHit &&
            handleLightBoxDirectHit &&
            zpid &&
            hmimhwAnalyticsData &&
            areRequiredDimensionsSet &&
            !alreadyDirectAutoOpen
        ) {
            handleLightBoxDirectHit();
            setAlreadyDirectAutoOpen(true);
        }
    }, [
        surfaceId,
        placementId,
        setIsLightboxOpen,
        zpid,
        areRequiredDimensionsSet,
        hmimhwAnalyticsData,
        handleLightBoxDirectHit,
        alreadyDirectAutoOpen,
        setRequiredCustomDimensions,
        isLightBoxDirectHit,
    ]);

    const onError = useYieldCallback((error) =>
        trackHmimhwPropertySearchError(placementId, surfaceId, error)
    );

    const handleAddressSelect = useYieldCallback(
        (params) => {
            if (params.zpid) {
                trackAddressSearch(
                    params.surfaceId || surfaceId,
                    params.placementId || placementId
                );
                setZpid(params.zpid);
            }
        },
        [placementId, surfaceId]
    );

    if (zpid && typeof document !== 'undefined') {
        return (
            <div className="scrollTarget" data-testid="hmimhw-home-detail-lightbox">
                <HomeDetailLightbox
                    zpid={zpid}
                    setZpid={setZpid}
                    zatlasEndpoint={zatlasEndpoint}
                    surfaceId={surfaceId}
                    placementId={placementId}
                    xConsumerUsername={xConsumerUsername}
                    isLightboxBackgroundLocked={isLightboxBackgroundLocked}
                    isCMS={isCMS}
                    setHmimhwAnalyticsData={setHmimhwAnalyticsData}
                />
            </div>
        );
    }
    return (
        <PropertySearch
            renderInput={(props) =>
                SearchInputWithButton({
                    handleAddressSelect,
                    placementId,
                    surfaceId,
                    options: props.options,
                    ...props,
                })
            }
            renderEmptyState={null}
            onSubmit={handleAddressSelect}
            onError={onError}
            renderError={ErrorBox}
            addressAutocompleteUrl={addressAutocompleteURL}
        />
    );
};

export default PropertySearchWrapper;
